import appConfig from '../../configs/appConfig';
import awsRumCustomEvents from '../../consts/aws/awsRumCustomEvents';
import badgeTypes from '../../consts/loyalty/badgeTypes';
import milestoneTypes from '../../consts/loyalty/milestoneTypes';
import quizQuestionTypes from '../../consts/loyalty/quizQuestionTypes';
import userSegmentTypes from '../../consts/loyalty/userSegmentTypes';
import userSurveyTypes from '../../consts/loyalty/userSurveyTypes';
import routes from '../../consts/routes';
import {strCode} from '../../consts/strCode';
import uamStatusTypes from '../../consts/uam/uamStatusTypes';
import badgesConfig from '../../containers/ClubPage/configs/badgesConfig';
import BADGES_CONFIG from '../../containers/ClubPage/configs/badgesConfig';
import flavoursConfig from '../../containers/ClubPage/configs/flavoursConfig';
import milestonesConfig from '../../containers/ClubPage/configs/milestonesConfig';
import PUZZLE_CONFIG from '../../containers/ClubPage/configs/puzzleConfig';
import CLUB_QUIZ_CONFIG from '../../containers/ClubPage/configs/quizConfig';
import CLUB_PAGE_REWARDS_CONTENT from '../../containers/ClubPage/configs/rewardsConfig';
import CLUB_PAGE_STORIES_CONTENT from '../../containers/ClubPage/configs/storiesConfig';
import {selectAuthTime} from '../../state/selectors/auth';
import {selectIsRetail} from '../../state/selectors/global';
import {
    selectBadgeData,
    selectBadges,
    selectCompletedBadges,
    selectConversionStatus,
    selectIsConsecutive3Days,
    selectLoyaltyUserData,
    selectQuizData,
    selectSortedBadges,
} from '../../state/selectors/loyalty';
import {selectCurrentFlowParameters} from '../../state/selectors/monitoring';
import {selectIsUserSessionInitialized} from '../../state/selectors/session';
import {getState} from '../../state/store';
import valueUtils from '../../utils/valueUtils';
import featuresService from '../features/featuresService';
import {formatString, t} from '../localization/localizationService';
import {calcDaysLeft, toMs} from './loyaltyRulesService';
import loyaltySourcingService from './loyaltySourcingService';

const getIsMilestoneCompleted = (milestoneType, badgeType) => {
    const badgeData = selectBadgeData(badgeType)(getState());

    if (badgeType && milestoneType) {
        return badgeData.completedMilestones?.includes(milestoneType);
    }

    const badges = selectBadges(getState());

    return Object.values(badges).some((badge) => badge.completedMilestones.includes(milestoneType));
};

export const getMilestoneData = (milestoneType, badgeType) => {
    if (!milestoneType) return {};

    const {
        milestones = [],
        lockedMilestoneIndex,
        lockedMilestoneTime,
        isLocked: isBadgeLocked,
    } = selectBadgeData(badgeType)(getState());

    const cfg = milestonesConfig[milestoneType];
    const idx = milestones.indexOf(milestoneType);

    if (idx === -1) {
        return {
            ...cfg,
            daysLeft: null,
            isLocked: false,
            milestoneType,
            isCompletedMilestone: false,
        };
    }
    const isCompleted = getIsMilestoneCompleted(milestoneType, badgeType);
    const hasLockedIndex = Number.isFinite(lockedMilestoneIndex);
    const isTargetMilestone = hasLockedIndex && lockedMilestoneIndex - 1 === idx;
    const isLockTimeInFuture = Boolean(lockedMilestoneTime) && Date.now() < toMs(lockedMilestoneTime);

    const isLocked = (() => {
        if (isBadgeLocked) return true;
        if (isTargetMilestone) return !lockedMilestoneTime || isLockTimeInFuture;
        return hasLockedIndex && idx > lockedMilestoneIndex - 1;
    })();

    const daysLeft = isTargetMilestone && isLockTimeInFuture ? calcDaysLeft(lockedMilestoneTime) : null;

    return {
        ...cfg,
        milestoneType,
        isCompletedMilestone: isCompleted,
        isLocked,
        daysLeft,
    };
};

export const getBadgesSummary = () => {
    const totalBadgesCount = selectSortedBadges(getState())?.length;
    const completedBadgesCount = selectCompletedBadges(getState())?.length;
    const progressPercent = (completedBadgesCount / totalBadgesCount) * 100;

    return {totalBadgesCount, completedBadgesCount, progressPercent};
};

export const transformBadgeToBody = ({badgeType, milestone}) => {
    let actualBadgeType = badgeType;
    let level = 'level1';

    const parentBadgeType = getParentBadgeType(badgeType);

    if (parentBadgeType) {
        actualBadgeType = parentBadgeType;

        const parentCfg = badgesConfig[parentBadgeType];
        const levelIndex = parentCfg.levels.findIndex((levelType) => levelType === badgeType);

        level = `level${levelIndex + 1}`;
    }

    const badgeData = selectBadgeData(badgeType)(getState());
    const milestoneIndex = badgeData?.milestones.findIndex((item) => item === milestone);

    return {
        badge: actualBadgeType,
        level,
        milestone: `milestone${milestoneIndex + 1}`,
        completeTs: Date.now(),
    };
};

const isFeatureAvailable = (featureCode) => {
    return featureCode ? featuresService.getIsFeatureEnabled(featureCode) : true;
};

const getBadgeConfig = (badgeType) => {
    return badgesConfig[badgeType] ?? {};
};

const isBadgeAvailable = (badgeType) => {
    if (badgeType === badgeTypes.SWITCH_MASTER)
        return getIsSwitchMasterBadgeAvailable() || getIsConversionRestartQuizAvailable();

    const {featureCode, isSubBadge} = getBadgeConfig(badgeType);

    const isAvailable = isFeatureAvailable(featureCode);

    return isAvailable && !isSubBadge;
};

const isBadgeProgressAvailable = (badgeType) => {
    const {featureCode, isSubBadge, levels} = getBadgeConfig(badgeType);

    if (levels) return false;

    const isAvailable = isFeatureAvailable(featureCode);

    if (isSubBadge) return isAvailable && getIsSwitchMasterBadgeAvailable();

    return isAvailable;
};

export const filterSortedBadges = (badges) =>
    Object.values(badges).filter(({badgeType}) => isBadgeAvailable(badgeType));

export const filterNotFinishedBadges = (badges) =>
    Object.values(badges).filter(
        ({badgeType, progress, isCompleted}) => isBadgeProgressAvailable(badgeType) && progress > 0 && !isCompleted
    );

export const filterNotStartedBadges = (badges) =>
    Object.values(badges).filter(({badgeType, progress}) => isBadgeAvailable(badgeType) && progress === 0);

export const filterCompletedBadges = (badges) => {
    return Object.values(badges).filter(({badgeType, isCompleted}) => isBadgeAvailable(badgeType) && isCompleted);
};

export const getQuizData = (quizType) => {
    const userSegmentArray = loyaltySourcingService.getUserSegmentTypes();
    const quizConfig = CLUB_QUIZ_CONFIG[quizType];
    const quizStore = selectQuizData(quizType)(getState()) ?? {};

    const segmentedQuizConfig = applySegmentArray(quizConfig, userSegmentArray);

    return {
        quizType,
        ...segmentedQuizConfig,
        slides: segmentedQuizConfig.slides?.map((s) => applySegmentArray(s, userSegmentArray)) ?? [],
        ...quizStore,
    };
};

const userSegmentValuesSet = new Set(Object.values(userSegmentTypes));

const isUserSegmentType = (value) => {
    return userSegmentValuesSet.has(value);
};

const applyConsecutive3Days = (merged) => {
    const isConsecutive3Days = selectIsConsecutive3Days(getState());
    let result = {...merged};

    if (result?.isConsecutive3Days) {
        if (isConsecutive3Days) {
            result = {...result, ...result.isConsecutive3Days};
        }
        delete result.isConsecutive3Days;
    }

    return result;
};

const applySegmentArray = (obj, segments) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;

    let merged = {...obj};

    if (Array.isArray(segments)) {
        segments.forEach((segment) => {
            if (merged[segment]) {
                merged = {...merged, ...merged[segment]};
            }
        });
    } else if (segments && merged[segments]) {
        merged = {...merged, ...merged[segments]};
    }

    merged = applyConsecutive3Days(merged);

    for (const key in merged) {
        if (Array.isArray(merged[key])) {
            merged[key] = processArrayItems(merged[key], segments);
        } else if (typeof merged[key] === 'object' && merged[key] !== null) {
            merged[key] = applySegmentArray(merged[key], segments);
        }
    }

    return merged;
};

const processArrayItems = (array, segments) => {
    return array.reduce((acc, item) => {
        const segmentedItem = applySegmentArray(item, segments);

        if (valueUtils.isEmpty(segmentedItem)) return acc;

        const isOnlySegmentObject = Object.keys(segmentedItem).every((itemKey) => isUserSegmentType(itemKey));

        if (isOnlySegmentObject) return acc;

        acc.push(segmentedItem);
        return acc;
    }, []);
};

export const getStoriesData = (storiesType) => {
    const userSegmentArray = loyaltySourcingService.getUserSegmentTypes();
    const config = CLUB_PAGE_STORIES_CONTENT?.[storiesType] || {};

    // Apply segment to the main config object first
    const segmentedConfig = applySegmentArray(config, userSegmentArray);

    return {
        ...segmentedConfig,
        stories: segmentedConfig.stories?.map((s) => applySegmentArray(s, userSegmentArray)) ?? [],
    };
};

export const getHasBlankMapping = (blanks, correctOptions) => {
    return correctOptions.some((opt) => opt.blankId) && blanks?.some((blank) => blank.correctOptionId);
};

const validateFillBlanksWithMapping = (correctOptions, picked) => {
    return correctOptions.every((correct) =>
        picked.some(
            (pickedItem) => pickedItem.blankId === correct.blankId && String(pickedItem.optionId) === String(correct.id)
        )
    );
};

const validateFillBlanksFlexibleOrder = (correctOptions, picked) => {
    const pickedOptionIds = picked.map((item) => String(item.optionId));
    const correctOptionIds = correctOptions.map((opt) => String(opt.id));

    return correctOptionIds.every((correctId) => pickedOptionIds.includes(correctId));
};

export const getIsAnswerCorrect = (slide, picked = []) => {
    const correctOptions = slide.options?.filter((o) => o.isCorrect) ?? [];

    if (!picked.length) return false;

    const isMultipleSelect = correctOptions.length > 1;
    const isFillBlanks = slide?.type === quizQuestionTypes.FILL_BLANKS;

    if (isFillBlanks) {
        if (picked.length !== correctOptions.length) return false;

        const hasBlankMapping = getHasBlankMapping(slide?.blanks, correctOptions);

        return hasBlankMapping
            ? validateFillBlanksWithMapping(correctOptions, picked)
            : validateFillBlanksFlexibleOrder(correctOptions, picked);
    }

    if (isMultipleSelect) {
        return picked.length === correctOptions.length && correctOptions.every((opt) => picked.includes(opt.id));
    }

    return picked[0] === correctOptions[0]?.id;
};

export const getIsAnswersCorrect = (selectedOptions, slides) => {
    return slides.every((slide) => {
        const picked = selectedOptions[slide.id] ?? [];

        return getIsAnswerCorrect(slide, picked);
    });
};

export const getPuzzleData = () => {
    return PUZZLE_CONFIG;
};

export const getBadgeImage = (badgeType, isInProgress) =>
    isInProgress ? BADGES_CONFIG?.[badgeType]?.progressImageSrc : BADGES_CONFIG?.[badgeType]?.imageSrc;

export const getIsConversionQuizAvailable = () => {
    return selectConversionStatus(getState()) === uamStatusTypes.NOT_STARTED;
};

export const getIsConversionRestartQuizAvailable = () => {
    return selectConversionStatus(getState()) === uamStatusTypes.FAILED;
};

export const getIsSwitchMasterBadgeAvailable = () => {
    const isConversionQuizPassed = selectConversionStatus(getState()) === uamStatusTypes.SUCCESS;
    const isSwitchMasterBadgeAvailable = appConfig.getIsSwitchMasterEnabled();

    return isConversionQuizPassed && isSwitchMasterBadgeAvailable;
};

export const getActiveStories = () => {
    for (const [storyType, story] of Object.entries(CLUB_PAGE_STORIES_CONTENT)) {
        const {milestoneType, badgeType, badgeLevelType} = story;
        const storyBadgeType = badgeLevelType || badgeType;

        // Skip stories without badge or milestone type
        if (!storyBadgeType || !milestoneType) continue;

        const isAvailable = isBadgeAvailable(badgeType); // Only main badge type

        // Skip unavailable badges
        if (!isAvailable) continue;

        const {isLocked} = getMilestoneData(milestoneType, storyBadgeType);

        // Skip locked stories
        if (isLocked) continue;

        const isCompleted = getIsMilestoneCompleted(milestoneType, storyBadgeType);

        // Return the first uncompleted story
        if (!isCompleted) {
            return storyType;
        }
    }

    return null;
};

export const getFirstStory = ({stories, isFeedExtract}) => {
    const [firstStory] = stories || [];

    if (!isFeedExtract) return firstStory;

    const [firstFeedStory] = firstStory?.feed || [];

    return firstFeedStory || firstStory;
};

export const waitPreconditions = async (m) => {
    const INTERVAL = 1000;
    const DISABLED_PAGES = [
        routes.LTE_CONNECTION_GUIDE_INTRO,
        routes.WIFI_CONNECTION_GUIDE_INTRO,
        routes.LTE_CONNECTION_GUIDE_STEPS,
        routes.WIFI_CONNECTION_GUIDE_STEPS,
        routes.DEVICE_REGISTRATION,
        routes.DEVICE_REGISTRATION_ERROR,
        routes.DEVICE_CONNECTION,
        routes.DEVICE_CONNECTION_ERROR,
        routes.DEVICE_CONNECTED,
    ];
    const DISABLED_FLOWS = [awsRumCustomEvents.ONBOARDING, awsRumCustomEvents.SET_UP];

    const isAllowed = () => {
        const state = getState();
        const currentPage = window.location.pathname;
        const {currentFlow} = selectCurrentFlowParameters(state);

        const pageOK = !DISABLED_PAGES.includes(currentPage);
        const flowOK = !DISABLED_FLOWS.includes(currentFlow);
        const isUserSessionInitialized = selectIsUserSessionInitialized(getState());
        const isRetail = selectIsRetail(getState());

        if (milestoneTypes.ADD_APP_TO_HOME_SCREEN === m) {
            return isUserSessionInitialized && !isRetail;
        }

        return pageOK && flowOK && isUserSessionInitialized && !isRetail;
    };

    if (isAllowed()) {
        return;
    }

    while (!isAllowed()) {
        await new Promise((res) => setTimeout(res, INTERVAL));
    }
};

export const formatConversionData = (data) => {
    const result = structuredClone(data);

    for (const key in result) {
        if (key !== userSurveyTypes.SOURCING) {
            result[key] = data?.[key]?.[0] ?? null;
        }
    }
    return result;
};

export const getCorrectQuizSlidesCount = (slides, selectedOptions = []) => {
    const correctSlides = slides.filter((slide) => {
        const correctOptions = slide.options.filter((option) => option.isCorrect).map((option) => option.id);
        const selectedOptionsForSlide = selectedOptions[slide.id] || [];

        return correctOptions.some((id) => selectedOptionsForSlide.includes(id));
    });

    return correctSlides.length;
};

export const getParentBadgeEntry = (badgeType) => {
    const parentEntry = Object.entries(badgesConfig).find(([, cfg]) => cfg.levels?.includes(badgeType)) || [];

    return {
        parentBadgeType: parentEntry[0],
        parentConfig: parentEntry[1],
    };
};

export const getParentBadgeType = (badgeType) => {
    const {parentBadgeType} = getParentBadgeEntry(badgeType);

    return parentBadgeType;
};

export const getParentBadgeTitleStr = (badgeType) => {
    const {parentConfig} = getParentBadgeEntry(badgeType);

    return parentConfig?.titleStrCode;
};

export const getSessionStartDate = () => {
    const {surveyResultTs} = selectLoyaltyUserData(getState());

    if (surveyResultTs) return surveyResultTs;

    return selectAuthTime(getState());
};

export const getFlavourSlides = (flavour) => {
    const config = flavoursConfig?.[flavour] || {};

    return Object.values(config);
};

const buildDescription = (childBadgeType, badgeTitleStrCode, isSubBadge, isNotEarned) => {
    const name = isSubBadge
        ? `${t(getParentBadgeTitleStr(childBadgeType))} ${t(badgeTitleStrCode)}`
        : t(badgeTitleStrCode);

    return formatString(
        isNotEarned
            ? strCode.CLUB_PAGE_REWARD_LIST_DESCRIPTION_NOT_EARNED
            : strCode.CLUB_PAGE_REWARD_LIST_DESCRIPTION_AVAILABLE,
        name
    );
};

export const getRewardsData = (reward, isNotEarned = false) => {
    const found = Object.entries(BADGES_CONFIG).find(([, cfg]) => cfg?.rewardType === reward);

    if (!found) return {};

    const [badgeType, {titleStrCode: badgeTitleStrCode, isSubBadge}] = found;
    const {titleStrCode} = CLUB_PAGE_REWARDS_CONTENT[reward] || {};

    return {
        titleStrCode,
        description: buildDescription(badgeType, badgeTitleStrCode, isSubBadge, isNotEarned),
    };
};

export const getRewardsDataByBadgeType = (badgeType, level, isNotEarned = false) => {
    const levelId = Number(String(level).split('level')[1]) - 1;
    const childType = BADGES_CONFIG[badgeType]?.levels?.[levelId] || badgeType;
    const badge = BADGES_CONFIG[childType];

    if (!badge) return {};

    const {titleStrCode: badgeTitleStrCode, rewardType, isSubBadge} = badge;
    const {titleStrCode} = CLUB_PAGE_REWARDS_CONTENT[rewardType] || {};

    return {
        titleStrCode,
        description: buildDescription(childType, badgeTitleStrCode, isSubBadge, isNotEarned),
    };
};

export default {
    getRewardsData,
    getRewardsDataByBadgeType,
};
